import { createContext, useContext, useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CheckCircle, XCircle, AlertTriangle, Info, RefreshCw } from 'lucide-react';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [conflictDialog, setConflictDialog] = useState(null);

  const showNotification = useCallback((type, title, message) => {
    setNotification({ type, title, message });
  }, []);

  const success = useCallback((message, title = 'Success') => {
    showNotification('success', title, message);
  }, [showNotification]);

  const error = useCallback((message, title = 'Error') => {
    showNotification('error', title, message);
  }, [showNotification]);

  const warning = useCallback((message, title = 'Warning') => {
    showNotification('warning', title, message);
  }, [showNotification]);

  const info = useCallback((message, title = 'Information') => {
    showNotification('info', title, message);
  }, [showNotification]);

  const confirm = useCallback((message, onConfirm, titleOrOptions = 'Confirm Action') => {
    // Backwards compatible: 3rd arg can be a title string OR an options object
    // Options: { title, confirmLabel, confirmClass, icon ('alert'|'override'), iconColor }
    const opts = typeof titleOrOptions === 'string'
      ? { title: titleOrOptions }
      : { title: 'Confirm Action', ...titleOrOptions };
    setConfirmDialog({
      title: opts.title,
      message,
      onConfirm,
      confirmLabel: opts.confirmLabel || 'Delete',
      confirmClass: opts.confirmClass || 'bg-red-600 hover:bg-red-700',
      iconColor: opts.iconColor || 'text-red-600',
    });
  }, []);

  const conflict = useCallback((onReload) => {
    setConflictDialog({ onReload });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(null);
  }, []);

  const closeConflictDialog = useCallback(() => {
    setConflictDialog(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmDialog?.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog(null);
  }, [confirmDialog]);

  const handleConflictReload = useCallback(() => {
    if (conflictDialog?.onReload) {
      conflictDialog.onReload();
    }
    setConflictDialog(null);
  }, [conflictDialog]);

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-emerald-600" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'info':
      default:
        return <Info className="w-6 h-6 text-blue-600" />;
    }
  };

  const getButtonClass = (type) => {
    switch (type) {
      case 'success':
        return 'bg-emerald-600 hover:bg-emerald-700';
      case 'error':
        return 'bg-red-600 hover:bg-red-700';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700';
      case 'info':
      default:
        return 'bg-blue-600 hover:bg-blue-700';
    }
  };

  return (
    <NotificationContext.Provider value={{ success, error, warning, info, confirm, conflict }}>
      {children}
      {/* Standard Notification Modal */}
      <AlertDialog open={!!notification} onOpenChange={(open) => !open && closeNotification()}>
        <AlertDialogContent data-testid="notification-modal">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              {notification && getIcon(notification.type)}
              <AlertDialogTitle className="text-lg font-semibold">
                {notification?.title}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-slate-600 mt-2">
              {notification?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={closeNotification}
              className={notification ? getButtonClass(notification.type) : ''}
              data-testid="notification-ok-button"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog Modal */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <AlertDialogContent data-testid="confirm-modal">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-6 h-6 ${confirmDialog?.iconColor || 'text-red-600'}`} />
              <AlertDialogTitle className="text-lg font-semibold">
                {confirmDialog?.title}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-slate-600 mt-2 whitespace-pre-line">
              {confirmDialog?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={closeConfirmDialog}
              data-testid="confirm-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className={confirmDialog?.confirmClass || 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-ok-button"
            >
              {confirmDialog?.confirmLabel || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conflict Dialog Modal */}
      <AlertDialog open={!!conflictDialog} onOpenChange={(open) => !open && closeConflictDialog()}>
        <AlertDialogContent data-testid="conflict-modal">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <RefreshCw className="w-6 h-6 text-amber-600" />
              <AlertDialogTitle className="text-lg font-semibold">
                Record Modified
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-slate-600 mt-2">
              This record has been modified by another user while you were editing. 
              Your changes cannot be saved. Please reload to see the latest version and try again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={closeConflictDialog}
              data-testid="conflict-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConflictReload}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="conflict-reload-button"
            >
              Reload Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
